import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';
import { RealEstateFilterOffices } from '@app-model/promotedContent';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { RealEstateOfficeOptionsService } from './real-estate-office-options.service';

@Component({
	selector: 'app-real-estate-office-multi-select',
	templateUrl: './real-estate-office-multi-select.component.html',
	styleUrls: ['./real-estate-office-multi-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RealEstateOfficeMultiSelectComponent),
			multi: true
		}
	]
})
export class RealEstateOfficeMultiSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	@Input() entityType!: string;
	@Input() entityId!: string;
	@Input() labelKey = 'promotedContents.realEstateFilterOptions.office.label';
	@Input() placeholderKey = 'promotedContents.realEstateFilterOptions.selectOption';
	@Input() selectedCountKey = 'searchProperties.filters.selectedCount';
	@Input() selectedListAriaLabelKey = 'searchProperties.filters.selectedOptions';
	@Input() removeSelectionAriaLabelKey = 'searchProperties.filters.removeSelection';
	@Input() appearance: 'fill' | 'outline' | 'standard' | 'legacy' = 'outline';
	@Input() searchDebounceMs = 250;
	@coerceBoolean() @Input() isDisabled = false;

	public disabled = false;
	public value: string[] = [];
	public options: string[] = [];
	public optionLabels: Record<string, string> = {};
	public searchTerm = '';

	private readonly destroy$ = new Subject<void>();
	private readonly searchTerm$ = new Subject<string>();
	private onChange: (value: string[]) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(
		private readonly officeOptionsService: RealEstateOfficeOptionsService,
		private readonly cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.searchTerm$
			.pipe(
				debounceTime(this.searchDebounceMs),
				distinctUntilChanged(),
				switchMap(searchTerm => {
					if (!this.entityType || !this.entityId) {
						return of([] as RealEstateFilterOffices[]);
					}

					return this.officeOptionsService.searchOffices(this.entityType, this.entityId, searchTerm).pipe(
						catchError(() => of([] as RealEstateFilterOffices[]))
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(offices => {
				this.assignOfficeOptions(offices);
			});

		this.loadOptions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const entityTypeChanged = !!changes['entityType'] && !changes['entityType'].firstChange;
		const entityIdChanged = !!changes['entityId'] && !changes['entityId'].firstChange;

		if (entityTypeChanged || entityIdChanged) {
			this.loadOptions();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	writeValue(value: string[] | null): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.ensureSelectedLabels();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	onSelectionChange(value: string[]): void {
		this.value = Array.isArray(value) ? [...value] : [];
		this.ensureSelectedLabels();
		this.onChange(this.value);
		this.onTouched();
		this.cdr.markForCheck();
	}

	onSearchTermChange(searchTerm: string): void {
		this.searchTerm = searchTerm;
		this.searchTerm$.next(searchTerm);
	}

	private loadOptions(): void {
		this.searchTerm$.next(this.searchTerm);
	}

	private assignOfficeOptions(offices: RealEstateFilterOffices[]): void {
		this.options = offices.map(office => office?.name).filter((name): name is string => !!name);
		this.optionLabels = this.options.reduce<Record<string, string>>((acc, option) => {
			acc[option] = option;
			return acc;
		}, {});
		this.ensureSelectedLabels();
		this.cdr.markForCheck();
	}

	private ensureSelectedLabels(): void {
		this.value.forEach(option => {
			if (option && !this.optionLabels[option]) {
				this.optionLabels[option] = option;
			}
		});
	}

	get isSelectDisabled(): boolean {
		return this.disabled || this.isDisabled || !this.entityType || !this.entityId;
	}
}
